import { Form, Input, Modal, Select, message } from 'antd';
import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { GENDERS, USER_ROLES } from 'constants/users';
import { useOrganizationsStateContext } from 'providers/Organizations';
import {
  fetchOrganizationById,
  updateOrganization
} from 'services/api/organizations';
import { fetchRoles } from 'services/api/roles';
import { assignUserRole, createUser } from 'services/api/users';
import { RoleListRow } from 'types/api/roles';

import useCeiling from 'hooks/useCeiling';
import { showErrorMessage } from 'utils/showErrorMessage';
import { roleAssignRefusal } from 'utils/ceiling';

/** The picker value that means "every organization you hold". */
const ALL_ORGANIZATIONS = 'all';

interface IProps {
  refetch(): void;
  onClose(): void;
  role?: string;
  id?: string;
}

const UsersModal: FC<IProps> = ({ id, role, refetch, onClose }) => {
  const [form] = Form.useForm();

  const closeModal = () => {
    onClose();
    form.resetFields();
  };

  const { results } = useOrganizationsStateContext();
  // Only a superadmin's All is a standing grant; see organizationOptions.
  const { isSuperAdmin } = useCeiling();

  const [roles, setRoles] = useState<RoleListRow[]>([]);
  // Assignment is no longer super_admin-only — every admin may grant a role
  // whose permissions are a subset of their own. Still admins only: the
  // endpoint writes a user_roles row scoped to `role = 'admin'`, so there is
  // nothing it could set for a translator or a doctor.
  const canAssignRole = role === USER_ROLES.admin;

  // Roles are flat: one call, no group step. The group select it replaces
  // was hidden whenever `role` was passed, and both callers always pass it.
  useEffect(() => {
    if (!canAssignRole) return;

    let cancelled = false;

    const load = async () => {
      try {
        const rows = await fetchRoles();
        if (!cancelled) setRoles(rows);
      } catch {
        // swallow — empty options on failure
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [canAssignRole]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    const load = async () => {
      try {
        const result = await fetchOrganizationById(id);
        if (cancelled || !result) return;
        form.setFieldsValue({
          name: result.name,
          additional_info: result.additional_info || ''
        });
      } catch {
        // swallow — empty form on failure
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [id, form]);

  /**
   * ALL, and what it means.
   *
   * The backend reads `allOrganizations` as "every organization THE
   * CREATOR IS ENTITLED TO" — their own and everything beneath them, which
   * for a superadmin is every organization there is.
   *
   * WHAT IT MEANS AFTERWARDS DEPENDS ON WHO PRESSED IT, and the note has
   * to say which. A superadmin's All is STANDING: the account is flagged,
   * and each organization created later is granted to it. A delegated
   * admin's All is a SNAPSHOT of their own entitlement, expanded to rows
   * and flagged with nothing — because standing would let one admin widen
   * another's access just by being added to an organization themselves,
   * without ever deciding to.
   *
   * Either way the difference is invisible once the dialog closes, which
   * is why it belongs under the field rather than in a log.
   *
   * The option is added here rather than taken from the picker's own list:
   * that list only carries 'all' for a superadmin, because the header's
   * 'all' is about what the operator is looking at. This one is about what
   * the new account gets, and it means something for every operator.
   */
  const organizationOptions = useMemo(
    () => [
      { label: 'All organizations', value: ALL_ORGANIZATIONS },
      ...results.filter(item => item.value !== ALL_ORGANIZATIONS)
    ],
    [results]
  );

  const organizations: string[] = Form.useWatch('organizations', form) ?? [];
  const isAllOrganizations = organizations.includes(ALL_ORGANIZATIONS);

  /**
   * ALL and a hand-picked list are different answers to one question, so
   * the field never holds both: picking ALL replaces the selection, and
   * picking anything else drops ALL. The backend would resolve the clash
   * by letting ALL win, which silently gives more than the narrower half
   * asked for — better that the field cannot say two things at once.
   */
  const onOrganizationsChange = useCallback(
    (value: string[]) => {
      const addedAll =
        value.includes(ALL_ORGANIZATIONS) &&
        value[value.length - 1] === ALL_ORGANIZATIONS;

      form.setFieldValue(
        'organizations',
        addedAll
          ? [ALL_ORGANIZATIONS]
          : value.filter(item => item !== ALL_ORGANIZATIONS)
      );
    },
    [form]
  );

  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (role !== USER_ROLES.admin) {
      form.setFieldsValue({
        organizationIds: undefined
      });
    }
  }, [form, role]);

  const onFinish = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (values: Record<string, any>) => {
      if (id) {
        try {
          setIsUpdating(true);
          await updateOrganization(id, values);
          refetch();
          closeModal();
        } catch (err) {
          showErrorMessage(err);
        } finally {
          setIsUpdating(false);
        }

        return;
      }

      // CreateUserDto IS CAMELCASE AND IT REQUIRES `role`. /users responses
      // come back snake_case through toSnake, which is why this form's
      // fields are named that way, but the REQUEST body is not renamed —
      // the DTO declares firstName/lastName and nothing maps to them, and
      // forbidNonWhitelisted rejects an unknown field outright rather than
      // dropping it. Sent as it stood, this was six validation errors and
      // a 400 every time.
      //
      // ORGANIZATIONS GO IN THE CREATE BODY NOW, as organizationIds. They
      // used to be a POST per organization after the account existed,
      // which left a window — and sometimes a permanent state — where a
      // new admin belonged to none. An admin in no organization can sign
      // in and sees nothing, because every admin query is scoped to what
      // the caller holds, so the API now refuses to create one that way.
      // Creating and joining in one call is also the only version that
      // cannot half-succeed.
      //
      // The RBAC role stays a second call: PUT /users/:id/role is its own
      // endpoint and the DTO has no field for it.
      const {
        role_id: roleId,
        first_name: firstName,
        last_name: lastName,
        organizations,
        ...rest
      } = values;

      const isAdmin = role === USER_ROLES.admin;
      const payload = {
        ...rest,
        role,
        firstName,
        lastName,
        // Ignored for the other three roles — a doctor or translator takes
        // its organization from the validated header — so it is only sent
        // where it means something.
        ...(isAdmin && Array.isArray(organizations)
          ? organizations.includes(ALL_ORGANIZATIONS)
            ? { allOrganizations: true }
            : { organizationIds: organizations.map(Number) }
          : {})
      };

      try {
        setIsCreating(true);
        const created = await createUser(payload);
        const createdId = created.data?.id;

        if (roleId && isAdmin) {
          try {
            await assignUserRole(createdId, Number(roleId));
          } catch (err) {
            // The account exists; only the role did not land. Say so rather
            // than reporting a failed creation.
            showErrorMessage(err);
            message.warning(
              'The account was created, but its role was not assigned. Set it from Change role.'
            );
          }
        }

        refetch();
        closeModal();
      } catch (err) {
        showErrorMessage(err);
      } finally {
        setIsCreating(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, refetch, role]
  );

  return (
    <Modal
      open
      title={id ? 'Edit' : 'Create'}
      onCancel={closeModal}
      width={600}
      onOk={form.submit}
      confirmLoading={isCreating || isUpdating}
      destroyOnClose
    >
      <Form form={form} onFinish={onFinish} layout="vertical" size="large">
        <Form.Item
          name="first_name"
          label="First name"
          rules={[
            {
              required: true
            },
            {
              max: 25,
              min: 3
            },
            {
              message: 'Only latin letters are allowed',
              pattern: /^[A-Za-z][A-Za-z\s-]*$/
            }
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="last_name"
          label="Last name"
          rules={[
            {
              required: true
            },
            {
              max: 25,
              min: 3
            },
            {
              message: 'Only latin letters are allowed',
              pattern: /^[A-Za-z][A-Za-z\s-]*$/
            }
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="gender" label="Gender" rules={[{ required: true }]}>
          <Select options={GENDERS} />
        </Form.Item>
        <Form.Item
          name="email"
          label="Email"
          rules={[{ required: true, type: 'email' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="phone"
          label="Phone number"
          rules={[{ required: true }]}
        >
          <Input />
        </Form.Item>
        {/*
          Admins only, and super_admins only. PUT /users/:id/role writes a
          user_roles row scoped to `role = 'admin'`, so there is no way to set
          one for a translator or a doctor — asking for it on those forms
          collected a value and then threw it away. The BE confirmed admin-only
          is deliberate: MUSP-383 is scoped to the admin panel and the other
          panels' permission rows are unverified.
        */}
        {canAssignRole && (
          <Form.Item name="role_id" label="Role" rules={[{ required: true }]}>
            <Select
              // Assignable, not editable: the row carries both answers, and
              // they differ on a system role — uneditable by everyone, still
              // grantable by a superadmin.
              options={roles
                .filter(item => !roleAssignRefusal(item))
                .map(item => ({
                  value: item.id,
                  label: item.label || item.name
                }))}
            />
          </Form.Item>
        )}
        {role === USER_ROLES.admin && (
          <Form.Item
            name="organizations"
            label="Organizations"
            rules={[{ required: true }]}
            extra={
              isAllOrganizations
                ? isSuperAdmin
                  ? 'Every organization, including ones created later — they are granted automatically until someone removes one.'
                  : 'Every organization you hold today. Ones added later are not included on their own.'
                : undefined
            }
          >
            <Select
              mode="multiple"
              options={organizationOptions}
              onChange={onOrganizationsChange}
            />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default memo(UsersModal);
